class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onSubmitHandler.bind(this));
    facetForm.addEventListener('submit', this.onSubmitForm.bind(this));

    // Add click handlers for filter dropdowns
    this.querySelectorAll('details').forEach(detail => {
      detail.addEventListener('click', this.onDetailClick.bind(this));
      detail.addEventListener('keyup', this.onKeyUpEscape);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  onSubmitForm(event) {
    event.preventDefault();
    this.onSubmitHandler(event);
  }

  onDetailClick(event) {
    const summaryElement = event.target.closest('summary');
    if (!summaryElement) return;
    
    event.preventDefault();
    const detailElement = summaryElement.parentNode;
    const isOpen = detailElement.hasAttribute('open');

    // Close all other details first
    this.querySelectorAll('details').forEach(detail => {
      if (detail !== detailElement) {
        detail.removeAttribute('open');
      }
    });

    // Toggle the clicked detail
    if (isOpen) {
      detailElement.removeAttribute('open');
    } else {
      detailElement.setAttribute('open', '');
      summaryElement.setAttribute('aria-expanded', true);
    }
  }

  onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGrid = document.getElementById('ProductGridContainer');
    document.getElementById('main-collection-product-grid')?.classList.add('loading');
    
    if (productGrid) productGrid.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    // Update URL first if needed
    if (updateURLHash) {
      const path = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
      window.location.href = path; // This will trigger a full page reload with the new filters
      return; // Stop here as we're reloading the page
    }

    // Only continue with AJAX if we're not updating URL
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          
          if (productGrid) productGrid.classList.remove('loading');
          if (countContainer) countContainer.classList.remove('loading');
          if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
          document.getElementById('main-collection-product-grid')?.classList.remove('loading');
        });
    });
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newProductGrid = doc.getElementById('ProductGridContainer');
    if (newProductGrid) {
      document.getElementById('ProductGridContainer').innerHTML = newProductGrid.innerHTML;
    }

    const newMainGrid = doc.getElementById('main-collection-product-grid');
    if (newMainGrid) {
      const mainGrid = document.getElementById('main-collection-product-grid');
      if (mainGrid) mainGrid.innerHTML = newMainGrid.innerHTML;
    }
  }

  static renderProductCount(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newCount = doc.getElementById('ProductCount');
    if (newCount) {
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      if (container) {
        container.innerHTML = newCount.innerHTML;
        container.classList.remove('loading');
      }
      if (containerDesktop) {
        containerDesktop.innerHTML = newCount.innerHTML;
        containerDesktop.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersPillsForm .js-filter');
    const currentFacetDetailsElements = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersPillsForm .js-filter');
    const mobileFacetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersFormMobile .js-filter');
    const currentMobileFacetDetailsElements = document.querySelectorAll('#FacetFiltersFormMobile .js-filter');

    // Desktop facets
    currentFacetDetailsElements.forEach((currentElement, index) => {
      const newElement = facetDetailsElements[index];
      if (newElement) {
        const wasOpen = currentElement.hasAttribute('open');
        currentElement.innerHTML = newElement.innerHTML;
        if (wasOpen) {
          currentElement.setAttribute('open', '');
          const summary = currentElement.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', true);
        }
      }
    });

    // Mobile facets
    currentMobileFacetDetailsElements.forEach((currentElement, index) => {
      const newElement = mobileFacetDetailsElements[index];
      if (newElement) {
        const wasOpen = currentElement.hasAttribute('open');
        currentElement.innerHTML = newElement.innerHTML;
        if (wasOpen) {
          currentElement.setAttribute('open', '');
          const summary = currentElement.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', true);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);
      if (activeFacetsElement && currentElement) {
        currentElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const targetElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);
      if (targetElement && currentElement) {
        currentElement.innerHTML = targetElement.innerHTML;
      }
    });
  }

  static updateURLHash(searchParams) {
    const path = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    history.pushState({ searchParams }, '', path);
  }

  static getSections() {
    const sections = [];
    const productGrid = document.getElementById('product-grid') || document.getElementById('main-collection-product-grid');
    if (productGrid) {
      sections.push({ section: productGrid.dataset.id });
    }
    return sections;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();
    
    // Handle each form field separately
    const params = new Map();
    
    for (const [key, value] of formData.entries()) {
      if (!value) continue;
      
      // For price filters, use the exact value multiplied by 100
      if (key.includes('price')) {
        searchParams.append(key, (parseInt(value, 10) * 100).toString());
        continue;
      }
      
      // Group multiple values for the same key (for checkboxes)
      if (!params.has(key)) {
        params.set(key, []);
      }
      params.get(key).push(value);
    }
    
    // Add grouped values to searchParams
    for (const [key, values] of params.entries()) {
      values.forEach(value => {
        searchParams.append(key, value);
      });
    }
    
    // Preserve the search query
    const currentParams = new URLSearchParams(window.location.search);
    const currentQuery = currentParams.get('q');
    if (currentQuery) {
      searchParams.set('q', currentQuery);
    }
    
    // Get the collection path
    const collection = window.location.pathname.match(/\/collections\/([^\/]+)/)?.[1];
    const finalPath = collection ? `/collections/${collection}` : window.location.pathname;
    
    // Render the page with the new filters
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    const searchParams = url.searchParams;
    
    // Preserve the search query
    const currentQuery = new URLSearchParams(window.location.search).get('q');
    if (currentQuery) {
      searchParams.set('q', currentQuery);
    }
    
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => {
        element.addEventListener('input', this.onRangeChange.bind(this));
        element.addEventListener('change', this.onRangeChange.bind(this));
      });
    
    const form = this.closest('form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    
    // Clear input fields on initialization
    this.querySelectorAll('input').forEach(input => {
      input.value = '';
    });
    
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    const input = event.currentTarget;
    // Only allow numbers
    let value = input.value.replace(/[^\d]/g, '');
    
    // Don't add any formatting, just use the raw value
    input.value = value;
    
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
  }

  onFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    
    // Get the inputs directly
    const minInput = this.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = this.querySelector('input[name="filter.v.price.lte"]');
    
    // Multiply values by 100 to match Shopify's currency format
    if (minInput?.value) {
      formData.set('filter.v.price.gte', (parseInt(minInput.value, 10) * 100).toString());
    }
    if (maxInput?.value) {
      formData.set('filter.v.price.lte', (parseInt(maxInput.value, 10) * 100).toString());
    }
    
    if (minInput?.value || maxInput?.value) {
      const searchParams = new URLSearchParams();
      
      // Add each parameter separately
      for (const [key, value] of formData.entries()) {
        if (value) {
          searchParams.append(key, value);
        }
      }
      
      const collection = window.location.pathname.match(/\/collections\/([^\/]+)/)?.[1];
      const finalPath = collection ? `/collections/${collection}` : window.location.pathname;
      
      window.location.href = `${finalPath}?${searchParams.toString()}`;
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    
    if (!minInput || !maxInput) return;

    // Set constraints without modifying the input values
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', '0');
    if (maxInput.value === '') minInput.setAttribute('max', minInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    if (!input.value) return;

    // Parse as integer but don't modify the value unless necessary
    const value = parseInt(input.value, 10);
    if (isNaN(value)) {
      input.value = '';
      return;
    }

    const min = parseInt(input.getAttribute('min') || 0, 10);
    const max = parseInt(input.getAttribute('max') || Infinity, 10);

    // Only modify the value if it's outside the valid range
    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
    if (value < 0) input.value = '0';
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
